import { isEqual, merge } from 'lodash';
import { useContext, useEffect, useMemo, useRef } from 'react';

import { useQuery } from 'utils/apollo';
import { usePrevious } from 'utils/hooks';

import { getFilterBy, getMultiSortBy, getSortBy, setUrlParams } from './paging';
import {
  type TDataTableContext,
  type TQueryFilter,
  type TQuerySort,
  type TUseServerDataInput,
} from './types';

import { DataTableContext } from './index';

export function useServerData(input: TUseServerDataInput) {
  const {
    setTotalCount,
    query,
    transform,
    columns,
    routeProps,
    onDataQueryChange,
    rowSelection,
    deferCount = false,
  } = input;

  const {
    pagination,
    filteredInfo,
    sortedInfo,
    updatePagination,
    initialRouteVariables,
    multiSortDef,
  }: TDataTableContext = useContext(DataTableContext);

  const isInitialFetchRef = useRef<boolean>(true);

  const {
    location,
    history,
    // Defines the client to route the request to
    client = undefined,
    // An array of filter parameters that will always be applied to requests coming from this table
    universalFilter = [],
    universalSortBy = [],
    usePartialData = false,
    notifyOnNetworkStatusChange = false,
    variables: propRouteVariables,
    skip = false,
    context,
  } = routeProps ?? {};
  const previousRouteProps = usePrevious(routeProps);

  const routeVariables = isInitialFetchRef.current
    ? merge(initialRouteVariables, propRouteVariables)
    : propRouteVariables;

  const filterBy: TQueryFilter[] = getFilterBy({ filters: filteredInfo, columns });
  const sortBy: TQuerySort[] = getSortBy(sortedInfo);
  const multiSortBy: TQuerySort[] = getMultiSortBy(multiSortDef);

  const pageSize: number = pagination && pagination.pageSize ? pagination.pageSize : 10;
  const current: number = pagination && pagination.current ? pagination.current : 1;
  const offset: number = pageSize * (current - 1);
  const cursorPagination: Boolean = context?.cursorPagination ?? false;

  const baseVariables = useMemo(() => {
    // filters: universalFilter takes precedence; filterBy entries with colliding key/operator pairs are dropped
    const universalFilterKeys = new Set(universalFilter.map(f => `${f.key}:${f.operator ?? ''}`));
    const filterByAllowed = filterBy.filter(
      f => !universalFilterKeys.has(`${f.key}:${f.operator ?? ''}`)
    );
    const resolvedFilterBy = Array.from(
      new Map(
        [...universalFilter, ...filterByAllowed].map(f => [
          `${f.key}:${f.operator ?? ''}:${JSON.stringify(f.values ?? [])}`,
          f,
        ])
      ).values()
    );

    // sorts: universalSortBy > sortBy > multiSortBy, all deduped by key
    const resolvedSortBy = Array.from(
      new Map(
        [...universalSortBy, ...sortBy, ...multiSortBy].reverse().map(sort => [`${sort.key}`, sort])
      ).values()
    ).reverse();

    return {
      ...routeVariables,
      filterBy: resolvedFilterBy,
      sortBy: resolvedSortBy,
    };
  }, [routeVariables, universalFilter, filterBy, universalSortBy, sortBy, multiSortBy]);

  // exclude count from items query if deferCount is true
  const variables = useMemo(
    () => ({
      ...baseVariables,
      ...(cursorPagination
        ? { after: String(offset), first: pageSize }
        : { limit: pageSize, offset }),
      ...(deferCount ? { includeCount: false } : {}),
    }),
    [baseVariables, deferCount, offset, cursorPagination, pageSize]
  );

  // variables for count-only query
  const countVariables = useMemo(
    () => ({ ...baseVariables, includeCount: true, includeItems: false }),
    [baseVariables]
  );

  // items query, excludes count if deferCount is true
  const {
    data = {},
    loading,
    networkStatus,
    refetch,
  } = useQuery(query, {
    client,
    context,
    notifyOnNetworkStatusChange,
    returnPartialData: usePartialData,
    variables,
    skip,
  });

  // deferred count query - run only if deferCount is true
  const { data: countData = {} } = useQuery(query, {
    client,
    context,
    variables: countVariables,
    fetchPolicy: 'cache-and-network',
    skip: skip || !deferCount,
  });

  /**
   * This effect hook takes the place of the onCompleted handler for both the item and count
   * queries.
   *
   * When items and count are queried separately (deferCount === true), their results are merged.
   * When the item query includes the count, all the data is already together.
   * After merging the data (if necessary), the logic is the same either way.
   */
  useEffect(() => {
    if (!data || Object.keys(data).length === 0) {
      return;
    }

    // merge item and count query data, if necessary
    const mergedData = deferCount ? { ...data, ...countData } : data;
    const dataForFindCount = transform ? transform(mergedData) : mergedData || {};

    // conditional to support GQL Connection spec query structure
    const totalCount = dataForFindCount?.totalCount ?? dataForFindCount?.pageInfo?.totalCount ?? 0;
    if (setTotalCount) {
      setTotalCount(totalCount);
    }
    const order = sortedInfo?.order;
    const columnKey = sortedInfo?.columnKey;

    // url updates to correspond to the new thing we are looking at
    setUrlParams(
      {
        ...routeVariables,
        filters: filteredInfo,
        pagination: { pageSize, current },
        sortOrder: { order, columnKey },
      },
      location,
      history
    );

    const countDataResolved = deferCount ? Object.keys(countData).length > 0 : true;
    const paginationUpdates: { total?: number; current?: number } = {};

    // Only update when pagination is configured (has a pageSize) and we have a real count.
    // Skips when pagination is disabled ({}) or when deferCount === true and count hasn't resolved yet.
    if (
      !!pagination &&
      pagination.pageSize &&
      pagination.total !== totalCount &&
      countDataResolved
    ) {
      paginationUpdates.total = totalCount;
    }

    /**
     * Set current page back to 1 if it is too high for the current number of records.
     * Only do this once we have a real count to avoid resetting to page 1 on every render while count is loading.
     */
    const offset = pageSize * (current - 1);
    if (
      offset >= totalCount &&
      countDataResolved &&
      !!pagination &&
      pagination.pageSize &&
      pagination.current !== 1
    ) {
      paginationUpdates.current = 1;
    }

    if (Object.keys(paginationUpdates).length) {
      updatePagination(paginationUpdates);
    }

    // keep interested parties synced up with the current state of the filter / sort situation
    if (onDataQueryChange) {
      onDataQueryChange({
        ...routeVariables,
        totalCount,
        filterBy,
        sortBy,
        isInitialFetch: isInitialFetchRef.current,
      });
    } else if (!deferCount && setTotalCount) {
      setTotalCount(totalCount);
    }

    if (isInitialFetchRef.current) {
      isInitialFetchRef.current = false;
    }

    /**
     * Itentionally limiting dependencies to data & countData to mimic behavior of useQuery's onCompleted
     * Other dependencies (e.g. pagination) will cause effect loops
     */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, JSON.stringify(countData)]);

  const transformedData = transform ? transform(data) : data || {};
  const items = transformedData.items ?? [];

  // Reset pagination when universal filter changes
  // TODO: This effect runs a lot because variables is a dependency, this could be optimized but there's dragons
  // in DataTable that need to be slain first. This works for now, and should be okay as long as universalFilter stays small.
  useEffect(() => {
    // If we don't have a previous route prop or we skipped it, we can't compare the filters
    if (!previousRouteProps || previousRouteProps?.skip) {
      return;
    }

    if (
      !isEqual(routeProps?.universalFilter, previousRouteProps?.universalFilter) ||
      !isEqual(routeProps?.variables, previousRouteProps?.variables)
    ) {
      updatePagination({ current: 1 });
      refetch({
        ...variables,
        offset: 0,
      });
      rowSelection?.onChange?.([], []);
    }
  }, [
    routeProps?.universalFilter,
    previousRouteProps?.universalFilter,
    routeProps?.variables,
    previousRouteProps?.variables,
    refetch,
    variables,
    rowSelection,
    updatePagination,
    previousRouteProps,
    previousRouteProps?.skip,
  ]);

  return {
    client,
    data: items,
    loading,
    networkStatus,
    refetch,
    filterBy,
    countLoading: deferCount && !Object.keys(countData).length,
  };
}
